import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../assets/sorisori-logo.png';

const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/products' },
  { label: 'Media', to: '/media' },
];

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    padding: theme.spacing(2.5, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '@media (max-width:900px)': {
      padding: theme.spacing(2, 2),
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: '-0.01em',
    textDecoration: 'none',
  },
  brandBadge: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  brandLogo: {
    width: 24,
    height: 24,
    objectFit: 'contain',
    display: 'block',
  },
  nav: {
    display: 'flex',
    gap: theme.spacing(4),
    position: 'relative',
    '@media (max-width:900px)': { display: 'none' },
  },
  desktopActions: {
    '@media (max-width:900px)': { display: 'none' },
  },
  mobileToggle: {
    display: 'none',
    color: '#fff',
    '@media (max-width:900px)': { display: 'inline-flex' },
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    background: 'rgba(8,8,10,0.96)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2.5, 3),
  },
  mobileTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
  },
  mobileNav: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  mobileLink: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 600,
    letterSpacing: '-0.5px',
    padding: theme.spacing(1.5, 0),
    textDecoration: 'none',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  mobileSubLink: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 16,
    fontWeight: 500,
    padding: theme.spacing(1, 0, 1, 2),
    textDecoration: 'none',
    cursor: 'pointer',
    display: 'block',
  },
  mobileFooter: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  mobileSocials: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  navItem: {
    position: 'relative',
    padding: '6px 0',
  },
  navLink: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'color 0.2s',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    '&:hover': { color: theme.palette.text.primary },
  },
  caret: {
    fontSize: 10,
    opacity: 0.7,
    transition: 'transform .2s',
  },
  caretOpen: { transform: 'rotate(180deg)' },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    minWidth: 140,
    paddingTop: 10,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 10,
    },
  },
  dropdownInner: {
    background: 'rgba(18,18,20,0.78)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 8,
    padding: 4,
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  dropdownItem: {
    display: 'block',
    padding: '8px 12px',
    borderRadius: 5,
    textDecoration: 'none',
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '-0.1px',
    cursor: 'pointer',
    transition: 'background .15s, color .15s',
    '&:hover': { background: 'rgba(255,255,255,0.06)', color: '#fff' },
  },
  signIn: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  socialIcon: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255,255,255,0.7)',
    textDecoration: 'none',
    borderRadius: 8,
    transition: 'color .2s, background .2s',
    '&:hover': {
      color: theme.palette.text.primary,
      background: 'rgba(255,255,255,0.06)',
    },
  },
  cta: {
    backgroundColor: theme.palette.primary.main,
    color: '#0a0a0a',
    borderRadius: 999,
    padding: theme.spacing(0.85, 2.25),
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      filter: 'brightness(0.95)',
      boxShadow: 'none',
    },
  },
}));

export default function Header() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <Box component="header" className={classes.root}>
      <Box component={Link} to="/" className={classes.brand}>
        <Box className={classes.brandBadge}>
          <img src={logo} alt="SoriSori AI" className={classes.brandLogo} />
        </Box>
        SoriSori AI
      </Box>

      <Box className={classes.nav}>
        {NAV_ITEMS.map((item) => {
          const hasSub = !!item.submenu;
          const isOpen = openMenu === item.label;
          return (
            <Box
              key={item.label}
              className={classes.navItem}
              onMouseEnter={() => hasSub && setOpenMenu(item.label)}
              onMouseLeave={() => hasSub && setOpenMenu(null)}
            >
              {hasSub ? (
                <Box component="span" className={classes.navLink}>
                  {item.label}
                  <span
                    className={`${classes.caret} ${isOpen ? classes.caretOpen : ''}`}
                  >
                    ▾
                  </span>
                </Box>
              ) : item.href ? (
                <Box
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.navLink}
                >
                  {item.label}
                </Box>
              ) : (
                <Box component={Link} to={item.to} className={classes.navLink}>
                  {item.label}
                </Box>
              )}
              {hasSub && isOpen && (
                <Box className={classes.dropdown}>
                  <Box className={classes.dropdownInner}>
                    {item.submenu.map((sub) => (
                      <Box
                        key={sub.label}
                        className={classes.dropdownItem}
                        onClick={() => {
                          setOpenMenu(null);
                          navigate(sub.to);
                        }}
                      >
                        {sub.label}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        className={classes.desktopActions}
      >
        <Box
          component="a"
          href="https://www.instagram.com/sorisori.ai/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          className={classes.socialIcon}
        >
          <InstagramIcon fontSize="small" />
        </Box>
        <Box
          component="a"
          href="https://www.youtube.com/@ytsorisori"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="YouTube"
          className={classes.socialIcon}
        >
          <YouTubeIcon fontSize="small" />
        </Box>
        <Button
          variant="contained"
          className={classes.cta}
          disableElevation
          href="https://app.sorisori.ai/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ml: 1.5 }}
        >
          Get Started
        </Button>
      </Stack>

      <IconButton
        aria-label="Open menu"
        className={classes.mobileToggle}
        onClick={() => setMobileOpen(true)}
      >
        <MenuIcon />
      </IconButton>

      {mobileOpen && (
        <Box className={classes.mobileMenu}>
          <Box className={classes.mobileTopRow}>
            <Box component={Link} to="/" className={classes.brand} onClick={closeMobile}>
              <Box className={classes.brandBadge}>
                <img src={logo} alt="SoriSori AI" className={classes.brandLogo} />
              </Box>
              SoriSori AI
            </Box>
            <IconButton
              aria-label="Close menu"
              onClick={closeMobile}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className={classes.mobileNav}>
            {NAV_ITEMS.map((item) => (
              <Box key={item.label}>
                {item.href ? (
                  <Box
                    component="a"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.mobileLink}
                    onClick={closeMobile}
                  >
                    {item.label}
                  </Box>
                ) : (
                  <Box
                    component={Link}
                    to={item.to}
                    className={classes.mobileLink}
                    onClick={closeMobile}
                  >
                    {item.label}
                  </Box>
                )}
                {item.submenu && item.submenu.map((sub) => (
                  <Box
                    key={sub.label}
                    component={Link}
                    to={sub.to}
                    className={classes.mobileSubLink}
                    onClick={closeMobile}
                  >
                    └ {sub.label}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          <Box className={classes.mobileFooter}>
            <Button
              variant="contained"
              className={classes.cta}
              disableElevation
              href="https://app.sorisori.ai/"
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
            >
              Get Started
            </Button>
            <Box className={classes.mobileSocials}>
              <Box
                component="a"
                href="https://www.instagram.com/sorisori.ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className={classes.socialIcon}
              >
                <InstagramIcon fontSize="small" />
              </Box>
              <Box
                component="a"
                href="https://www.youtube.com/@ytsorisori"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className={classes.socialIcon}
              >
                <YouTubeIcon fontSize="small" />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
